import { FC, useMemo, useCallback } from 'react'
import styles from 'components/features/socialFeed/ComposeMentionsSuggests/ComposeMentionsSuggests.styl'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { SuggestUser, ComposeId } from 'interfaces/feed'
import { suggestedUsersDataSelector, suggestedUsersStartsFromSelector } from 'selectors/feedSelectors'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import { Avatar } from 'components/features/socialFeed/Avatar/Avatar'
import { FeedUserBadges } from 'components/features/socialFeed/FeedUserInfo/FeedUserBadges'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import cn from 'classnames'
import { composeExecCommandActionCreator, feedComposeMentionActionCreator } from 'actions/feedComposeActions'
import { mentionize } from 'helpers/feedCommonHelpers'
import { useSuggestActiveElement } from 'helpers/composeHooks'
import { Decoration } from 'helpers/composeHelpers'
import { createComposePostAuthorUidSelector } from 'selectors/feedComposeSelectors'


export const SUGGESTS_KEYS_TO_COMMAND_MAP: Record<string, string> = {
  Enter: 'useSuggest',
  ArrowUp: 'prevSuggest',
  ArrowDown: 'nextSuggest',
}

type ComposeMentionsSuggestsProps = {
  composeId: ComposeId
  search: string
  decoration: Decoration
}

export const ComposeMentionsSuggests: FC<ComposeMentionsSuggestsProps> = (props) => {
  const { search, composeId, decoration } = props

  const dispatch = useStoreDispatch()

  const composePostAuthorUidSelector = useMemo(createComposePostAuthorUidSelector, [])
  const authorUid = useStoreSelector((state) => composePostAuthorUidSelector(state, props))

  const isLoading = useStoreSelector((state) => suggestedUsersDataSelector(state).isLoading)
  const suggests = useStoreSelector<SuggestUser[]>((state) => suggestedUsersStartsFromSelector(state, search))

  const handleSelect = useCallback<(user: SuggestUser) => void>((user) => {
    dispatch(composeExecCommandActionCreator({
      composeId,
      command: {
        type: 'replaceText',
        text: `${ mentionize(user.username) } `,
        start: decoration.start,
        end: decoration.end,
      },
    }))

    dispatch(feedComposeMentionActionCreator({
      mention: user.username,
      uid: user.uid,
      composeId,
    }))

  }, [dispatch, composeId, decoration.start, decoration.end])

  const [activeIndex, setActiveIndex] = useSuggestActiveElement(suggests, props, handleSelect)

  const children = useMemo(() => {
    const items = suggests.map((user, index) => (
      <ActionMenuItem
        key={ user.uid }
        isHighlighted={ index === activeIndex }
        fullWidth
        preventHover
        onClick={ () => handleSelect(user) }
        onMouseMove={ () => setActiveIndex(index) }
        onMouseEnter={ () => setActiveIndex(index) }
      >
        <div className={ cn(styles.mention, index === activeIndex && styles.active) }>
          <div className={ styles.avatar }>
            <Avatar
              avatar={ user.avatar ? { image: user.avatar } : null }
              name={ user.username }
              preventDonut
            />
          </div>
          { user.username }
          <div className={ styles.badges }>
            <FeedUserBadges
              badges={ user.badges }
              mode='comment'
            />
          </div>
          { authorUid === user.uid && (
            <div className={ styles.author }>
              <FeedUserBadges
                badges={ [{
                  slug: 'author',
                  text: 'Author',
                }] }
                mode='comment'
                isPostAuthor
              />
            </div>
          )}
        </div>
      </ActionMenuItem>
    ))

    if (isLoading) {
      items.push(
        <ActionMenuItem
          key='loading'
          isDisabled
        >
          Loading...
        </ActionMenuItem>
      )
    }

    if (items.length === 0) {
      items.push(
        <ActionMenuItem
          key='loading'
          isDisabled
        >
          Member not found
        </ActionMenuItem>
      )
    }

    return items
  }, [suggests, isLoading, activeIndex, authorUid, handleSelect, setActiveIndex])

  return (
    <ActionMenu onHide={ () => { /* do nothing */ } }>
      { children }
    </ActionMenu>
  )
}
